import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const courses = [
  {
    id: 'system-design',
    title: 'System Design Masterclass',
    tagline: 'Design at scale. Think like a senior engineer.',
    icon: '🏗️',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)',
    accentColor: '#818cf8',
    accentBg: 'rgba(99,102,241,0.12)',
    accentBorder: 'rgba(99,102,241,0.25)',
    description:
      'The most structured path to mastering system design. Used by engineers who got offers at Google, Amazon, Microsoft and more.',
    features: [
      'Bite-sized visual lessons — no fluff',
      'Step-by-step design playbooks',
      '13+ real production case studies',
      'Live architecture diagrams',
      'Quizzes & assessments',
      'Q&A with instructors',
    ],
    cta: 'Get Access',
    href: '/course',
    badge: 'Most Popular',
  },
  {
    id: 'lld',
    title: 'Low Level Design Masterclass',
    tagline: 'Write code that engineers actually respect.',
    icon: '⚙️',
    gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)',
    accentColor: '#34d399',
    accentBg: 'rgba(16,185,129,0.10)',
    accentBorder: 'rgba(16,185,129,0.22)',
    description:
      'Go deep on object-oriented design, design patterns, and the SOLID principles used in production systems at scale.',
    features: [
      'Design patterns — 20+ covered in depth',
      'SOLID principles with real examples',
      'UML diagrams and class structures',
      'Code organisation at scale',
      'Machine coding exercises',
      'Interview walkthroughs',
    ],
    cta: 'Get Access',
    href: '/course',
    badge: null,
  },
  {
    id: 'dp',
    title: 'Dynamic Programming Masterclass',
    tagline: 'Crack the hardest DSA interviews with confidence.',
    icon: '🧩',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
    accentColor: '#fb923c',
    accentBg: 'rgba(249,115,22,0.10)',
    accentBorder: 'rgba(249,115,22,0.22)',
    description:
      'Stop memorising solutions. Learn the patterns that let you solve any DP problem from first principles.',
    features: [
      'Pattern-first approach — not problem-first',
      'Visual breakdown of every problem type',
      '100+ interview-focused problems',
      'Memoisation & tabulation mastery',
      'Space optimisation techniques',
      'Company-wise problem sets',
    ],
    cta: 'Get Access',
    href: '/course',
    badge: null,
  },
];

const trusts = [
  { icon: '🛡️', title: '30-Day Guarantee', desc: 'Full refund if you\'re not satisfied — no questions asked.' },
  { icon: '⚡', title: 'Lifetime Access', desc: 'Buy once, access forever. All future updates included.' },
  { icon: '✅', title: 'Certificate', desc: 'Shareable certificate of completion for your LinkedIn.' },
  { icon: '🎯', title: '23,000+ Students', desc: 'Trusted by engineers preparing for FAANG and beyond.' },
];

const CoursesPage = () => {
  const cardRefs = useRef([]);
  const [visible, setVisible] = useState([]);

  useEffect(() => {
    // Stagger cards in on load — no need to wait for scroll
    courses.forEach((_, i) => {
      setTimeout(() => setVisible((v) => [...new Set([...v, i])]), i * 120);
    });
  }, []);

  return (
    <div className="cp-page">
      <Navbar />

      {/* ── Hero ── */}
      <section className="cp-hero">
        <div className="cp-hero-glow cp-hero-glow-blue" />
        <div className="cp-hero-glow cp-hero-glow-purple" />
        <div className="cp-hero-inner">
          <span className="cp-hero-badge">Interview Prep · Crafted by Engineers</span>
          <h1 className="cp-hero-title">
            The courses that actually<br />
            <span className="cp-hero-accent">get you hired.</span>
          </h1>
          <p className="cp-hero-sub">
            Built by engineers from Amazon, Microsoft &amp; Bloomberg who've conducted 200+ interviews —
            and know exactly what the bar looks like.
          </p>
        </div>
      </section>

      {/* ── Course Cards ── */}
      <section className="cp-courses">
        {courses.map((course, i) => (
          <div
            key={course.id}
            ref={(el) => (cardRefs.current[i] = el)}
            className={`cp-card ${visible.includes(i) ? 'cp-card-in' : ''}`}
            style={{ '--accent': course.accentColor, '--accent-bg': course.accentBg, '--accent-border': course.accentBorder, transitionDelay: `${i * 0.08}s` }}
          >
            {/* Left gradient strip */}
            <div className="cp-card-strip" style={{ background: course.gradient }} />

            {/* Card body */}
            <div className="cp-card-inner">
              {/* Header */}
              <div className="cp-card-header">
                <div className="cp-card-icon" style={{ background: course.accentBg, border: `1px solid ${course.accentBorder}` }}>
                  {course.icon}
                </div>
                <div className="cp-card-header-text">
                  {course.badge && (
                    <span className="cp-card-badge" style={{ color: course.accentColor, background: course.accentBg, borderColor: course.accentBorder }}>
                      {course.badge}
                    </span>
                  )}
                  <h2 className="cp-card-title">{course.title}</h2>
                  <p className="cp-card-tagline">{course.tagline}</p>
                </div>
              </div>

              {/* Divider */}
              <div className="cp-card-divider" />

              {/* Description + Features */}
              <div className="cp-card-body">
                <p className="cp-card-desc">{course.description}</p>
                <ul className="cp-card-features">
                  {course.features.map((f, fi) => (
                    <li key={fi}>
                      <span className="cp-feature-check" style={{ color: course.accentColor }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="cp-card-footer">
                <a
                  href={course.href}
                  className="cp-card-cta"
                  style={{ background: course.gradient }}
                >
                  {course.cta}
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ── Trust signals ── */}
      <section className="cp-trust">
        {trusts.map((t, i) => (
          <div key={i} className="cp-trust-item">
            <span className="cp-trust-icon">{t.icon}</span>
            <div>
              <p className="cp-trust-title">{t.title}</p>
              <p className="cp-trust-desc">{t.desc}</p>
            </div>
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
};

export default CoursesPage;
